import type {
    ApolloClientOptions,
    DocumentNode,
    MutationHookOptions,
    MutationTuple,
    NormalizedCacheObject,
    OperationVariables,
    PossibleTypesMap,
    QueryHookOptions,
    QueryResult,
    ServerParseError,
} from '@apollo/client';
import {
    ApolloClient,
    ApolloLink,
    defaultDataIdFromObject,
    HttpLink,
    InMemoryCache,
    useMutation as apolloUseMutation,
    useQuery as apolloUseQuery,
} from '@apollo/client';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import type { NetworkError } from '@apollo/client/errors';
import { relayStylePagination } from '@apollo/client/utilities';
import { has, memoize } from 'lodash-es';
import fetch from 'cross-fetch';
import type { MockApolloClient } from 'mock-apollo-client';
import { createMockClient as _createMockClient } from 'mock-apollo-client';
import { useEffect } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import possibleTypesJson from '../possible-types.json';
import { graphqlContextLink } from './graphql-context-link';
import warning from './logger';
import { checkResponseForHttpErrors, IgnorableHttpError } from '../graph/http-helper';

interface ClientConfigOptions {
    headers?: Record<string, string>;
}

declare global {
    interface Window {
        __APOLLO_DEVTOOLS_GLOBAL_HOOK__?: boolean;
    }
}

const possibleTypes = possibleTypesJson as PossibleTypesMap;

// Hides the 'Download the Apollo DevTools for a better development experience...' console message.
if (!window.__APOLLO_DEVTOOLS_GLOBAL_HOOK__) {
    window.__APOLLO_DEVTOOLS_GLOBAL_HOOK__ = true;
}

function isServerParserError(networkError?: NetworkError): networkError is ServerParseError {
    return (
        networkError !== undefined &&
        networkError !== null &&
        'response' in networkError &&
        'bodyText' in networkError
    );
}

export function graphQLErrorHandler({
    graphQLErrors,
    networkError,
    operation,
}: ErrorResponse): void {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, ...extra }) => {
            if (has(extra, 'extensions.code')) {
                // Any error that has a `code` is not unexpected and can be ignored.
                // This code is present when, for example, Python throws a `GraphQLExtendedError`.
                return;
            }

            warning(`[GraphQL error]: Message: ${message}`, extra);
        });
    }
    // NetworkError's without a response are not worth logging in as they never left the
    // user's computer due to no internet or canceled by the browser.
    if (isServerParserError(networkError)) {
        try {
            checkResponseForHttpErrors(networkError.response);
        } catch (e) {
            if (!(e instanceof IgnorableHttpError)) {
                warning(`[Network error]: ${String(networkError)}`, {
                    status: networkError.response.status,
                    url: networkError.response.url,
                    body: networkError.bodyText,
                    operation,
                });
            }
        }
    }
}

export function createClientConfig(
    mock: boolean,
    options: ClientConfigOptions = {}
): ApolloClientOptions<NormalizedCacheObject> {
    const { headers } = options;
    const config: ApolloClientOptions<NormalizedCacheObject> = {
        cache: new InMemoryCache({
            possibleTypes,
            typePolicies: {
                Query: {
                    fields: {
                        customAlarms: relayStylePagination(),
                    },
                },
                Walkthrough: {
                    keyFields: ['name'],
                },
                ShareType: {
                    keyFields: ['entityId', 'entityType', 'forUser'],
                },
                SwarmFeatures: {
                    keyFields: ['deviceName'],
                },
                Config: {
                    keyFields(object) {
                        // We do not want configs with no id in our cache.
                        if (!object.id) {
                            return false;
                        }

                        return defaultDataIdFromObject(object);
                    },
                },
                SoundMeasuringPointType: {
                    fields: {
                        notificationConfig: {
                            merge: true,
                        },
                    },
                },
                NotificationConfig: {
                    fields: {
                        ldenSchedules: {
                            merge<T>(_existing: T, incoming: T) {
                                return incoming; // Replace existing with incoming.
                            },
                        },
                    },
                },
            },
        }),
        devtools: { enabled: true },
    };

    if (!mock) {
        config.link = ApolloLink.from([
            onError(graphQLErrorHandler),
            graphqlContextLink,
            new HttpLink({
                uri: '/graphql/',
                credentials: 'same-origin',
                fetch,
                headers,
            }),
        ]);
    }

    return config;
}

export const getClient = memoize(() => new ApolloClient(createClientConfig(false)));

export function createMockClient(options?: ClientConfigOptions): MockApolloClient {
    return _createMockClient(createClientConfig(true, options));
}

// Checks if given string is a type identifier provided by GraphQL.
export function isTypeIdentifier(string: string): boolean {
    return string === '__typename';
}

export function useBoundaryQuery<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables
>(
    query: DocumentNode,
    options?: QueryHookOptions<TData, TVariables>
): QueryResult<TData, TVariables> {
    const { showBoundary } = useErrorBoundary();
    const result = apolloUseQuery<TData, TVariables>(query, options);

    useEffect(() => {
        if (result.error) {
            showBoundary(result.error);
        }
    }, [result.error, showBoundary]);

    return result;
}

export function useBoundaryMutation<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables
>(
    mutation: DocumentNode,
    options?: MutationHookOptions<TData, TVariables>
): MutationTuple<TData, TVariables> {
    const { showBoundary } = useErrorBoundary();
    const [mutate, state] = apolloUseMutation<TData, TVariables>(mutation, options);

    useEffect(() => {
        if (state.error) {
            showBoundary(state.error);
        }
    }, [state.error, showBoundary]);

    return [mutate, state];
}
